import { useCallback, useMemo, useRef } from 'react';

import { GroupBase, MenuListProps } from 'react-select';

import { useVirtualizer } from '@tanstack/react-virtual';
import isEqual from 'fast-deep-equal';

import { useConfiguration } from 'components/select/virtual/hooks';
import { flattenOptions } from 'components/select/virtual/utilities';

export const useVirtual = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  options,
}: Pick<MenuListProps<Option, IsMulti, Group>, 'children' | 'options'>) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const { estimatedSize, flattenedChildren } = useConfiguration({ children });
  const count = useMemo(() => flattenedChildren.length, [flattenedChildren.length]);

  const flattenedOptions: (Option | Group)[] = useMemo(() => {
    return flattenOptions(options);
  }, [options]);

  const currentIndex = useCallback(
    (option: Option) => {
      return flattenedOptions.findIndex((flattened) => isEqual(flattened, option));
    },
    [flattenedOptions]
  );

  const virtualizer = useVirtualizer({
    count,
    estimateSize: () => estimatedSize,
    getScrollElement: () => parentRef.current,
    overscan: 5,
  });

  return {
    currentIndex,
    flattenedChildren,
    items: virtualizer.getVirtualItems(),
    measureRef: virtualizer.measureElement,
    parentRef,
    scrollTo: virtualizer.scrollToIndex,
    totalSize: virtualizer.getTotalSize(),
  };
};
